<template lang="pug">
.judgement-template-select-tab(v-cloak)
  .mb-3
  span.font-weight-bold 選択中の様式：
  span {{ selectedTemplateName }}
  .mb-2
  TemplateSelectTable(
    v-model="items"
    :fields="fields"
    :isLoading="isLoading"
    :filter="filter"
    :selectValue.sync="_selectValue"
    radioName="judgement-radio"
    @onPreview="onPreview"
  )

  .register-row(v-show="errorMessage")
    .register-group
      .register-value
        .error-message {{ errorMessage }}

  .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
    // 検索
    .search-filter.d-flex.mr-1.align-items-center
      b-input-group.search-filter-input-group.mr-1(size="md")
        b-input-group-prepend
          b-input-group-text.search-filter-icon
            .search-icon
        b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="application-template-select-search-filter-help" title="検索項目について")
        template(v-slot:text)
          div 検索項目は以下になります。
          div ・様式名
  .mb-2
  .box-footer.register-footer
    .buttons.m-0
      button.btn.update-btn(@click="onUpdate" type="button") 変更

  JudgementTemplatePreviewModal(v-model="showPreviewModal" :template="previewTargetTemplate" :templateName="previewTargetTemplateName"
    :templateInformation="previewTargetTemplateInformation")
</template>

<script lang="ts">
import { Component, Inject, Watch, Prop, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";

import { userStore } from "@/store/store";

import TemplatesService, { AdmissionJudgementTemplatesGetModel as TemplateGetModel, Template } from "@/commons/services/AdmissionJudgementTemplates";
import { FacilityGetModel } from "@/commons/services/Facilities";

import HelpIcon from "@/pc/basics/HelpIcon.vue";
import TemplateSelectTable, { FilterSetting, TemplateSelectTableField, TemplateSelectTableItem } from "@/pc/containers/waiters/TemplateSelectTable.vue";
import JudgementTemplatePreviewModal from "@/pc/containers/waiters/JudgementTemplatePreviewModal.vue";

/**
 * ### 入所評価判定様式タブコンポーネント
 */
@Component({
  components: {
    HelpIcon,
    TemplateSelectTable,
    JudgementTemplatePreviewModal
  }
})
export default class JudgementTemplateSelectTab extends Vue {
  /** 親コンポーネントのバリデーターをInjectionする */
  @Inject("$validator") $validator!: Validator;

  /** 入所申込テンプレートサービス */
  @Inject("AdmissionJudgementTemplatesService") templatesService!: TemplatesService;

  @Prop()
  value!: FacilityGetModel;
  @Prop()
  errorMessage!: string;

  /** 列名 */
  fields: Array<TemplateSelectTableField> = [];
  /** 行情報 */
  items: Array<TemplateSelectTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "" };

  /** 選択中のテンプレート名 */
  selectedTemplateName: string = "";
  /** 入居テンプレートMAP(key:id value:テンプレート) */
  templateMap: Map<number, TemplateGetModel> = new Map<number, TemplateGetModel>();

  /** プレビューモーダルを表示するか否か */
  showPreviewModal: boolean = false;
  /** プレビューモーダルで表示するテンプレート */
  previewTargetTemplate: Template | null = null;
  /** プレビューモーダルで表示するテンプレート名 */
  previewTargetTemplateName: string = "";
  /** プレビューモーダルで表示するテンプレートの説明 */
  previewTargetTemplateInformation: string = "";

  get _selectValue(): string {
    if (this.value == null || this.value.useAdmissionJudgementTemplateId == null) {
      return "";
    }
    return `${this.value.useAdmissionJudgementTemplateId}`;
  }

  set _selectValue(newValue: string) {
    if (newValue == null) {
      return;
    }

    this.value.useAdmissionJudgementTemplateId = Number(newValue);
    return;
  }

  @Watch("value.useAdmissionJudgementTemplateId")
  public changeSelectedTemplateId(newValue: string): void {
    if (newValue == null) {
      return;
    }

    const template: TemplateGetModel | undefined = this.templateMap.get(Number(newValue));
    this.selectedTemplateName = template != undefined ? template.name : "";
    return;
  }

  /**
   * ### vue lifecycle mounted
   *
   * 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<TemplateSelectTableField> = this.createFields();
    this.fields = fields;

    // 動的に変更する画面情報を作成する
    this.items = [];

    await this.createView();

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<TemplateSelectTableField> {
    let fields: Array<TemplateSelectTableField> = [];

    fields.push({ key: "templateName", label: "様式名", sortable: false, sortDirection: "desc", class: "template-name" });
    fields.push({ key: "preview", label: "内容", sortable: false, sortDirection: "desc", class: "preview text-center" });

    return fields;
  }

  /**
   * ### 画面情報作成
   *
   * - 選択テンプレートを初期化する
   * - 選択対象のテンプレートを取得し、テーブルの行を作成する
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    const templates: Array<TemplateGetModel> = await this.getTemplates();
    const items: Array<TemplateSelectTableItem> = this.createItems(templates);
    this.items = items;

    const templateMap: Map<number, TemplateGetModel> = this.createTemplateMap(templates);
    this.templateMap = templateMap;

    if (this.value && this.value.useAdmissionJudgementTemplateId) {
      this.changeSelectedTemplateId(`${this.value.useAdmissionJudgementTemplateId}`);
    }

    this.isLoading = false;

    return;
  }

  /**
   * ### 入居申込みテンプレートを取得する
   */
  public async getTemplates(): Promise<Array<TemplateGetModel>> {
    const response: Array<TemplateGetModel> = await this.templatesService.gets(userStore);
    return response;
  }

  /**
   * ### 行情報を作成する
   */
  public createItems(templates: Array<TemplateGetModel>): Array<TemplateSelectTableItem> {
    let items: Array<TemplateSelectTableItem> = [];

    for (let i = 0; i < templates.length; i++) {
      let isFirst: boolean = i == 0 ? true : false;

      items.push({
        id: `${templates[i].id}`,
        name: templates[i].name,
        information: templates[i].information,
        isFirst: isFirst,
        template: templates[i].template
      });
    }

    return items;
  }

  /**
   * ### テンプレートのMAPを作成する
   */
  public createTemplateMap(templates: Array<TemplateGetModel>): Map<number, TemplateGetModel> {
    let templateMap: Map<number, TemplateGetModel> = new Map<number, TemplateGetModel>();

    for (const template of templates) {
      templateMap.set(template.id, template);
    }

    return templateMap;
  }

  /**
   * ### テンプレートをプレビュー表示する
   */
  public onPreview(item: TemplateSelectTableItem): void {
    this.previewTargetTemplate = item.template as Template;
    this.previewTargetTemplateName = item.template != undefined ? item.name : "";
    this.previewTargetTemplateInformation = item.template != undefined ? item.information : "";
    this.showPreviewModal = true;
    return;
  }

  /**
   * ### 更新ボタン押下イベント
   */
  public onUpdate(): void {
    if (!this.value.useAdmissionJudgementTemplateId) {
      this.$emit("update:errorMessage", "様式は必須選択です");
      return;
    }

    this.$emit("update");

    return;
  }
}
</script>

<style lang="scss" scoped>
.judgement-template-select-tab {
  .box-footer {
    padding: 10px 0 0 0 !important;

    .update-btn {
      width: 120px;
    }
  }
}
</style>
